// Phase 9 remainder — Inventory page.
//
// Per-course inventory list: product, on-hand, reorder threshold, low-stock
// chip when on-hand ≤ threshold, distributor URL button. Admin/supe can
// add + edit + delete rows. Yearly N-P-K rollup rendered below the table.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Product, UserRole } from '@greeniq/shared';
import { ApiError, api } from '../api/client.js';
import { listProducts } from '../api/chemical.js';
import { useMe } from '../context/MeContext.js';

const WRITE_ROLES: UserRole[] = ['ageye_admin', 'superintendent', 'assistant_super'];

interface InventoryItem {
  id: number;
  productId: number;
  onHandQty: string | number;
  unit: string;
  reorderThresholdQty: string | number | null;
  distributorUrl: string | null;
  notes: string | null;
  productName: string;
  productCategory: string;
  productManufacturer: string | null;
  productSignalWord: string | null;
}

interface NutrientBucket {
  month: string;
  zoneId: number | null;
  zoneName: string | null;
  nLb: number;
  pLb: number;
  kLb: number;
  applications: number;
  productLb: number;
}

export function InventoryPage() {
  const { courseSlug } = useParams();
  const { me } = useMe();
  const role: UserRole | undefined = me?.courses.find((c) => c.courseSlug === courseSlug)?.role;
  const canEdit = role ? WRITE_ROLES.includes(role) : false;

  const [items, setItems] = useState<InventoryItem[] | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [nutrients, setNutrients] = useState<NutrientBucket[]>([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!courseSlug) return;
    try {
      const [inv, prods, nuts] = await Promise.all([
        api.get<{ items: InventoryItem[] }>(`/courses/${courseSlug}/inventory`),
        listProducts(courseSlug, { limit: 500 }),
        api.get<{ buckets: NutrientBucket[] }>(`/courses/${courseSlug}/nutrients?year=${year}`),
      ]);
      setItems(inv.items);
      setProducts(prods.products);
      setNutrients(nuts.buckets);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  useEffect(() => {
    void load();
  }, [courseSlug, year]);

  if (error) return <div className="p-8 text-[color:var(--color-error)]">{error}</div>;
  if (items === null) return <div className="p-8 text-fg-muted">Loading…</div>;

  const lowStock = items.filter((i) => {
    const onHand = Number(i.onHandQty);
    const threshold = i.reorderThresholdQty !== null ? Number(i.reorderThresholdQty) : null;
    return threshold !== null && threshold > 0 && onHand <= threshold;
  });

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <Link to={`/app/${courseSlug}/chemical`} className="text-sm text-greeniq-400 hover:underline">
        ← Chemical &amp; Inventory
      </Link>

      <div className="mt-3 flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="uppercase tracking-wider text-xs text-greeniq-400 font-semibold">Phase 9</p>
          <h1 className="mt-1 text-3xl md:text-4xl font-display font-bold text-fg">Inventory</h1>
          <p className="mt-1 text-fg-muted">
            On-hand quantities + low-stock alerts + distributor reorder links.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowAdd((v) => !v)}
            className="px-4 py-2 rounded-md bg-greeniq-500 text-[#0b1014] text-sm font-semibold hover:bg-greeniq-400"
          >
            {showAdd ? 'Close' : '+ Add inventory'}
          </button>
        )}
      </div>

      {lowStock.length > 0 && (
        <div className="mt-4 p-3 rounded-md bg-warning-muted border border-[color:var(--color-warning)]/30 text-sm text-[color:var(--color-warning)]">
          <strong>{lowStock.length}</strong> product{lowStock.length === 1 ? '' : 's'} at or below reorder threshold.
        </div>
      )}

      {canEdit && showAdd && courseSlug && (
        <InventoryEditor
          courseSlug={courseSlug}
          products={products}
          existing={null}
          onSaved={() => {
            setShowAdd(false);
            void load();
          }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      <div className="mt-6 overflow-auto bg-bg-card rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border bg-bg-elevated">
              <th className="px-3 py-2 font-semibold text-fg-muted">Product</th>
              <th className="px-3 py-2 font-semibold text-fg-muted">Category</th>
              <th className="px-3 py-2 font-semibold text-fg-muted text-right">On hand</th>
              <th className="px-3 py-2 font-semibold text-fg-muted text-right">Reorder at</th>
              <th className="px-3 py-2 font-semibold text-fg-muted">Status</th>
              <th className="px-3 py-2 font-semibold text-fg-muted"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-fg-muted">
                  {canEdit ? 'No inventory tracked yet. Click "Add inventory" to get started.' : 'No inventory tracked yet.'}
                </td>
              </tr>
            )}
            {items.map((item) => {
              const onHand = Number(item.onHandQty);
              const threshold = item.reorderThresholdQty !== null ? Number(item.reorderThresholdQty) : null;
              const isLow = threshold !== null && threshold > 0 && onHand <= threshold;
              return (
                <tr key={item.id} className="border-b border-border last:border-b-0">
                  <td className="px-3 py-2">
                    <div className="font-semibold">{item.productName}</div>
                    <div className="text-xs text-fg-muted">{item.productManufacturer}</div>
                  </td>
                  <td className="px-3 py-2 capitalize">{item.productCategory.replace(/_/g, ' ')}</td>
                  <td className="px-3 py-2 text-right font-mono">{onHand} {item.unit}</td>
                  <td className="px-3 py-2 text-right font-mono text-fg-muted">
                    {threshold !== null ? `${threshold} ${item.unit}` : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {isLow ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-warning-muted text-[color:var(--color-warning)]">
                        Low stock
                      </span>
                    ) : onHand > 0 ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-success-muted text-[color:var(--color-success)]">
                        OK
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-bg-elevated text-fg-secondary">
                        Out
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {item.distributorUrl && (
                      <a
                        href={item.distributorUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-greeniq-400 hover:underline mr-3"
                      >
                        Reorder →
                      </a>
                    )}
                    {canEdit && courseSlug && (
                      <InventoryRowActions
                        courseSlug={courseSlug}
                        item={item}
                        products={products}
                        onSaved={load}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Nutrient rollup section */}
      <section className="mt-10">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-xl font-display font-semibold text-fg">Nutrient rollup · {year}</h2>
          <label className="text-xs text-fg-muted">
            Year
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="ml-2 border border-border rounded-md py-1 px-2 text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
        </div>
        <p className="mt-1 text-sm text-fg-muted">
          Parsed N-P-K from fertilizer applications × product % × lbs applied. Plan-limit comparisons land in slice 2.
        </p>
        {nutrients.length === 0 ? (
          <p className="mt-4 text-sm text-fg-muted">No fertilizer applications logged for {year} yet.</p>
        ) : (
          <NutrientRollupTable buckets={nutrients} />
        )}
      </section>
    </div>
  );
}

function NutrientRollupTable({ buckets }: { buckets: NutrientBucket[] }) {
  const perZone = useMemo(() => {
    const map = new Map<string, { zoneName: string; nLb: number; pLb: number; kLb: number; productLb: number; applications: number }>();
    for (const b of buckets) {
      const key = b.zoneName ?? 'course-wide';
      let bucket = map.get(key);
      if (!bucket) {
        bucket = { zoneName: key, nLb: 0, pLb: 0, kLb: 0, productLb: 0, applications: 0 };
        map.set(key, bucket);
      }
      bucket.nLb += b.nLb;
      bucket.pLb += b.pLb;
      bucket.kLb += b.kLb;
      bucket.productLb += b.productLb;
      bucket.applications += b.applications;
    }
    return Array.from(map.values()).sort((a, b) => b.nLb - a.nLb);
  }, [buckets]);

  return (
    <div className="mt-4 overflow-auto bg-bg-card rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-border bg-bg-elevated">
            <th className="px-3 py-2 font-semibold text-fg-muted">Zone</th>
            <th className="px-3 py-2 font-semibold text-fg-muted text-right">Apps</th>
            <th className="px-3 py-2 font-semibold text-fg-muted text-right">Product lb</th>
            <th className="px-3 py-2 font-semibold text-fg-muted text-right">N lb</th>
            <th className="px-3 py-2 font-semibold text-fg-muted text-right">P lb</th>
            <th className="px-3 py-2 font-semibold text-fg-muted text-right">K lb</th>
          </tr>
        </thead>
        <tbody>
          {perZone.map((p) => (
            <tr key={p.zoneName} className="border-b border-border last:border-b-0">
              <td className="px-3 py-2">{p.zoneName}</td>
              <td className="px-3 py-2 text-right">{p.applications}</td>
              <td className="px-3 py-2 text-right font-mono">{p.productLb.toFixed(1)}</td>
              <td className="px-3 py-2 text-right font-mono font-semibold">{p.nLb.toFixed(2)}</td>
              <td className="px-3 py-2 text-right font-mono">{p.pLb.toFixed(2)}</td>
              <td className="px-3 py-2 text-right font-mono">{p.kLb.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InventoryRowActions({
  courseSlug,
  item,
  products,
  onSaved,
}: {
  courseSlug: string;
  item: InventoryItem;
  products: Product[];
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  async function remove() {
    if (!confirm(`Remove ${item.productName} from inventory?`)) return;
    await api.delete(`/courses/${courseSlug}/inventory/${item.id}`);
    onSaved();
  }
  return (
    <>
      <button onClick={() => setEditing(true)} className="text-greeniq-400 hover:underline mr-2">Edit</button>
      <button onClick={() => void remove()} className="text-[color:var(--color-error)] hover:underline">Remove</button>
      {editing && (
        <InventoryEditor
          courseSlug={courseSlug}
          products={products}
          existing={item}
          onSaved={() => { setEditing(false); onSaved(); }}
          onCancel={() => setEditing(false)}
        />
      )}
    </>
  );
}

function InventoryEditor({
  courseSlug,
  products,
  existing,
  onSaved,
  onCancel,
}: {
  courseSlug: string;
  products: Product[];
  existing: InventoryItem | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [productId, setProductId] = useState<number | ''>(existing?.productId ?? '');
  const [onHand, setOnHand] = useState(String(existing?.onHandQty ?? ''));
  const [unit, setUnit] = useState(existing?.unit ?? 'lb');
  const [threshold, setThreshold] = useState(String(existing?.reorderThresholdQty ?? ''));
  const [url, setUrl] = useState(existing?.distributorUrl ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (productId === '') return;
    setSaving(true);
    try {
      await api.put(`/courses/${courseSlug}/inventory`, {
        productId: Number(productId),
        onHandQty: Number(onHand || 0),
        unit,
        reorderThresholdQty: threshold ? Number(threshold) : null,
        distributorUrl: url || null,
        notes: notes || null,
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-4 p-4 bg-bg-card rounded-lg border border-border grid gap-3 md:grid-cols-3">
      <label className="text-xs md:col-span-3">
        Product
        <select
          value={productId}
          onChange={(e) => setProductId(e.target.value === '' ? '' : Number(e.target.value))}
          disabled={existing !== null}
          className="mt-1 w-full border border-border rounded-md py-2 px-2 text-sm"
        >
          <option value="">Select…</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <label className="text-xs">
        On hand
        <input type="number" step="0.1" value={onHand} onChange={(e) => setOnHand(e.target.value)}
          className="mt-1 w-full border border-border rounded-md py-2 px-2 text-sm" />
      </label>
      <label className="text-xs">
        Unit
        <select value={unit} onChange={(e) => setUnit(e.target.value)}
          className="mt-1 w-full border border-border rounded-md py-2 px-2 text-sm">
          <option value="lb">lb</option>
          <option value="oz">oz</option>
          <option value="fl oz">fl oz</option>
          <option value="gal">gal</option>
          <option value="kg">kg</option>
          <option value="g">g</option>
          <option value="unit">unit</option>
        </select>
      </label>
      <label className="text-xs">
        Reorder at
        <input type="number" step="0.1" value={threshold} onChange={(e) => setThreshold(e.target.value)}
          className="mt-1 w-full border border-border rounded-md py-2 px-2 text-sm" />
      </label>
      <label className="text-xs md:col-span-3">
        Distributor URL
        <input type="url" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://…"
          className="mt-1 w-full border border-border rounded-md py-2 px-2 text-sm" />
      </label>
      <label className="text-xs md:col-span-3">
        Notes
        <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
          className="mt-1 w-full border border-border rounded-md py-2 px-2 text-sm" />
      </label>
      <div className="md:col-span-3 flex items-center gap-2">
        <button
          onClick={() => void save()}
          disabled={saving || productId === ''}
          className="px-3 py-2 rounded-md bg-greeniq-500 text-[#0b1014] text-sm font-semibold hover:bg-greeniq-400 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onCancel} className="text-sm text-fg-muted hover:text-fg">Cancel</button>
      </div>
    </div>
  );
}
